import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";

// Pages
import { LoginPage, RegisterPage } from "./pages/auth/LoginPage";
import { ForgotPasswordPage, ResetPasswordPage } from "./pages/auth/PasswordResetPages";
import { DashboardPage } from "./pages/dashboard/DashboardPage";
import { GuestReviewsPage, HostRepliesPage } from "./pages/reviews/ReviewJobsListPage";
import { ReviewDetailPage } from "./pages/reviews/ReviewDetailPage";
import { BookingsPage } from "./pages/bookings/BookingsPage";
import { EligibleBookingsPage } from "./pages/bookings/EligibleBookingsPage";
import { Beds24ConfigPage } from "./pages/config/Beds24ConfigPage";
import { SettingsPage } from "./pages/settings/SettingsPage";
import { LogsPage } from "./pages/logs/LogsPage";
import { ExtensionPage } from "./pages/extension/ExtensionPage";
import { IssuesPage } from "./pages/issues/IssuesPage";
import { IssueDetailPage } from "./pages/issues/IssueDetailPage";
import { ReportsPage } from "./pages/reports/ReportsPage";
import { QualityInsightsPage } from "./pages/quality/QualityInsightsPage";
import { ContractorsPage } from "./pages/contractors/ContractorsPage";
import { CalendarPage } from "./pages/calendar/CalendarPage";
import { MissionsPage } from "./pages/missions/MissionsPage";

// Protected route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F9F9] flex items-center justify-center">
        <div className="animate-pulse text-slate-400">Chargement...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Public route wrapper (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F9F9] flex items-center justify-center">
        <div className="animate-pulse text-slate-400">Chargement...</div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPasswordPage />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password"
        element={
          <PublicRoute>
            <ResetPasswordPage />
          </PublicRoute>
        }
      />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/guest-reviews"
        element={
          <ProtectedRoute>
            <GuestReviewsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/host-replies"
        element={
          <ProtectedRoute>
            <HostRepliesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/review/:jobId"
        element={
          <ProtectedRoute>
            <ReviewDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bookings"
        element={
          <ProtectedRoute>
            <BookingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/eligible"
        element={
          <ProtectedRoute>
            <EligibleBookingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/beds24"
        element={
          <ProtectedRoute>
            <Beds24ConfigPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/logs"
        element={
          <ProtectedRoute>
            <LogsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/extension"
        element={
          <ProtectedRoute>
            <ExtensionPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/issues"
        element={
          <ProtectedRoute>
            <IssuesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/issues/:commentId"
        element={
          <ProtectedRoute>
            <IssueDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <ReportsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/quality-insights"
        element={
          <ProtectedRoute>
            <QualityInsightsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/contractors"
        element={
          <ProtectedRoute>
            <ContractorsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/calendar"
        element={
          <ProtectedRoute>
            <CalendarPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/missions"
        element={
          <ProtectedRoute>
            <MissionsPage />
          </ProtectedRoute>
        }
      />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              background: '#fff',
              border: '1px solid #e2e8f0',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
